import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

import { useMapEditor } from '../MapEditorContext';
import { useCanUseMapEditor } from '../useCanUseMapEditor';
import { useLayoutPublish } from '../useLayoutPublish';
import { MAX_LAYOUT_JSON_CHARS } from '../mapEditorUnreal';
import { useLayoutPlaqueStyles } from './styles';

/**
 * The layout's own controls: **Publish** and **Undo all**.
 *
 * A plaque under the Scene panel rather than a section of the right-hand panel, and that is the whole
 * point of it. The right-hand panel is now only ever about one segment — its picture, its name, its
 * surfaces — so a Publish button inside it read as publishing *that segment*, which is not a thing
 * this feature can do. Both of these are about the whole layout, so they live where the whole layout
 * does: under the tree that lists it.
 *
 * Three things are deliberately **not** here and must not be re-added:
 *
 *  - **A status line of its own.** Whether the layout is a draft nobody else can see is now said by
 *    the one status on the BrandingBar, because the layout is saved onto the same configuration
 *    record as everything else that row reports on. While it was said in both places the two
 *    disagreed on the same screen: this plaque read "only you can see this" while the bar read "All
 *    changes saved", each half true on its own.
 *  - **Start over.** Clearing the chain in one click has no undo of its own — Revert only exists once
 *    something has been published to go back to — so an author who cleared a draft they had never
 *    published had no way back to it at all. Removing segments one at a time is the same operation
 *    with a way back after each step.
 *  - **The From above / Walk it toggle.** The walk is entered per segment now, through **Take me
 *    there** on the segment the author is working on, and left through the banner that says what they
 *    are standing in. A global toggle answered "show me the layout" and could not answer "show me
 *    this room", which is the question an author standing in a seven-room chain actually has.
 */
export const LayoutPlaque = () => {
  const { t: translate } = useTranslation();
  const styles = useLayoutPlaqueStyles();
  const { isDirty, revertDraft, canRevert } = useMapEditor();
  const { publish, oversizeBytes } = useLayoutPublish();

  // Gated on the map having a room kit at all, not just on the feature flag — see useCanUseMapEditor.
  // The plaque does not render on a map without one, so this is here for the buttons rather than for
  // the plaque.
  const canSpawnLayout = useCanUseMapEditor();

  return (
    <Box sx={styles.root} data-testid="map-editor-layout-plaque">
      <Box sx={styles.actions}>
        <Button
          sx={[
            styles.action,
            isDirty && canSpawnLayout && styles.actionPrimary,
          ]}
          onClick={publish}
          disabled={!isDirty || !canSpawnLayout}
          data-testid="map-editor-publish"
        >
          {isDirty
            ? translate('editorMode.mapEditorPublish', 'Publish')
            : translate('editorMode.mapEditorPublished', 'Published')}
        </Button>
        <Button
          sx={styles.action}
          onClick={revertDraft}
          disabled={!canRevert}
          data-testid="map-editor-undo-all"
        >
          {translate('editorMode.mapEditorUndoAll', 'Undo all')}
        </Button>
      </Box>

      {oversizeBytes > 0 && (
        <Box sx={styles.oversize}>
          <WarningAmberOutlinedIcon sx={styles.oversizeIcon} />
          <Typography sx={styles.oversizeText}>
            {translate(
              'editorMode.mapEditorOversize',
              'This layout is {{bytes}} bytes, over the {{limit}} the world accepts. Nothing was published. Use a finish on every surface at once instead of on single segments, or make the walk shorter.',
              { bytes: oversizeBytes, limit: MAX_LAYOUT_JSON_CHARS },
            )}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default LayoutPlaque;
